/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
*/

import React, { useRef, useMemo, useContext, createContext, useEffect } from 'react'
import { useGLTF, Merged } from '@react-three/drei'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'

const context = createContext()
export function Instances({ children, ...props }) {
  const { nodes, materials } = useGLTF('/models/Gantry.glb')
  
  // Disable frustum culling on all gantry meshes
  React.useEffect(() => {
    if (!nodes) return
    Object.values(nodes).forEach((node) => {
      if (node && node.isMesh) {
        node.frustumCulled = false
        node.renderOrder = 100 // Higher renderOrder to ensure rendering after ground
      }
    })
  }, [nodes])
  
  const instances = useMemo(
    () => ({
      ClampQuart: nodes['ClampQuart-01'],
      ClampSupport: nodes['Clamp-Support-01'],
      Arm: nodes.Arm001,
      Balcony: nodes.Balcony01,
      Beam: nodes.Beam,
      BeamCross: nodes['Beam-Cross01'],
      Lamp: nodes.Lamp01,
      Rack: nodes.Rack01,
      Fence: nodes.Fence01,
      Lamppost: nodes.Lamp_post01,
      Rail: nodes.Rail01,
    }),
    [nodes]
  )
  
  // Load and apply AO maps to gantry materials
  const aoMaps = useTexture({
    ClampArm: '/textures/gantry_ao/Clamp-Arm_Clamp-Arm_AO.png',
    ClampBase: '/textures/gantry_ao/Clamp-Base_Clamp-Base_AO.png',
    ClampQuart: '/textures/gantry_ao/Clamp-Quart_Clamp_AO.png',
    Environment: '/textures/gantry_ao/Environment_Environment_AO.png',
    Fuel: '/textures/gantry_ao/Fuel_Fuel_AO.png',
    Shack: '/textures/gantry_ao/Shack_Low_Shack_AO.png',
  })
  
  if (materials && aoMaps) {
    Object.entries(aoMaps).forEach(([aoKey, aoMap]) => {
      if (aoMap) aoMap.flipY = false
      Object.keys(materials).forEach((matKey) => {
        // Match material names with AO map keys
        if (matKey.toLowerCase().includes(aoKey.toLowerCase())) {
          materials[matKey].aoMap = aoMap
          materials[matKey].aoMapIntensity = 1.0
          materials[matKey].needsUpdate = true
        }
      })
    })
  }
  return (
    <Merged meshes={instances} {...props} frustumCulled={false} castShadow receiveShadow>
      {(instances) => <context.Provider value={instances} children={children} />}
    </Merged>
  )
}

export function Model(props) {
  const instances = useContext(context)
  const { nodes, materials } = useGLTF('/models/Gantry.glb')
  const fuelMeshRef = useRef()
  
  // Disable frustum culling on all direct mesh renders
  React.useEffect(() => {
    if (!nodes) return
    Object.values(nodes).forEach((node) => {
      if (node && node.isMesh) {
        node.frustumCulled = false
        node.renderOrder = 100 // Higher renderOrder to ensure rendering after ground
      }
    })
  }, [nodes])
  
  // Special handling for Fuel material - ensure it renders on top
  React.useEffect(() => {
    if (fuelMeshRef.current && materials.Fuel) {
      fuelMeshRef.current.renderOrder = 200 // Even higher for Fuel specifically
      materials.Fuel.transparent = true
      // Make sure fuel doesn't write to depth so it doesn't occlude other
      // transparent objects behind it (like bushes). Keep depthTest so
      // it still respects scene depth for opaque fragments.
      materials.Fuel.depthWrite = false
      materials.Fuel.depthTest = true
      materials.Fuel.premultipliedAlpha = false
      materials.Fuel.alphaTest = 0.1
    }
  }, [materials])
  
  // Ensure Environment material casts shadows properly
  React.useEffect(() => {
    if (materials.Environment) {
      materials.Environment.shadowSide = THREE.BackSide
      materials.Environment.needsUpdate = true
    }
  }, [materials])
  
  // Ensure all instance meshes cast and receive shadows
  React.useEffect(() => {
    if (!nodes) return
    Object.values(nodes).forEach((node) => {
      if (node && node.isMesh) {
        node.castShadow = true
        node.receiveShadow = true
      }
    })
  }, [nodes])

  return (
    <group {...props} dispose={null}>
      <group name="Scene">
        <group name="Clamp">
          <instances.ClampQuart name="ClampQuart-01" />
          <instances.ClampQuart name="ClampQuart-02" rotation={[0, -1.571, 0]} />
          <instances.ClampQuart name="ClampQuart-03" rotation={[Math.PI, 0, Math.PI]} />
          <instances.ClampQuart name="ClampQuart-04" rotation={[0, 1.571, 0]} />
          <group
            name="CTRL_LP_Clamp_01"
            position={[1.186, 2.635, -1.184]}
            rotation={[0, Math.PI / 4, 0]}>
            <instances.ClampSupport
              name="Clamp-Support-01"
              position={[-1.676, -2.635, -0.001]}
              rotation={[0, -Math.PI / 4, 0]}
            />
          </group>
          <group
            name="CTRL_LP_Clamp_02"
            position={[1.184, 2.635, 1.186]}
            rotation={[0, -Math.PI / 4, 0]}>
            <instances.ClampSupport
              name="Clamp-Support-02"
              position={[-1.676, -2.635, -0.001]}
              rotation={[0, -Math.PI / 4, 0]}
            />
          </group>
          <group
            name="CTRL_LP_Clamp_03"
            position={[-1.186, 2.635, 1.184]}
            rotation={[Math.PI, -Math.PI / 4, Math.PI]}>
            <instances.ClampSupport
              name="Clamp-Support-03"
              position={[-1.676, -2.635, -0.001]}
              rotation={[0, -Math.PI / 4, 0]}
            />
          </group>
          <group
            name="CTRL_LP_Clamp_4"
            position={[-1.184, 2.635, -1.186]}
            rotation={[-Math.PI, Math.PI / 4, -Math.PI]}>
            <instances.ClampSupport
              name="Clamp-Support-04"
              position={[-1.676, -2.635, -0.001]}
              rotation={[0, -Math.PI / 4, 0]}
            />
          </group>
          <group name="POI_LP_Clamp" position={[1.261, 1, -1.247]} />
          <group name="POS_LP_ClampCam" position={[9.373, 4.719, -4.965]} />
          <group name="TAR_LP_Clamp" position={[1.455, 1.395, 0.198]} />
        </group>
        <group name="Arm" position={[0.005, 0.637, -1.95]}>
          <group name="CTRL_LP_Arm">
            <mesh
              name="Arm-top-bottom"
              castShadow
              receiveShadow
              geometry={nodes['Arm-top-bottom'].geometry}
              material={materials['Clamp-Arm']}
            />
            <instances.Arm name="Arm001" />
            <instances.Arm name="Arm002" position={[0, 0.872, 0]} />
            <instances.Arm name="Arm003" position={[0, 1.743, 0]} />
            <instances.Arm name="Arm004" position={[0, 2.615, 0]} />
            <instances.Arm name="Arm005" position={[0, 3.486, 0]} />
            <instances.Arm name="Arm006" position={[0, 4.358, 0]} />
            <instances.Arm name="Arm007" position={[0, 5.229, 0]} />
            <instances.Arm name="Arm008" position={[0, 6.101, 0]} />
          </group>
        </group>
        <mesh
          name="Clamp_Base"
          castShadow
          receiveShadow
          geometry={nodes.Clamp_Base.geometry}
          material={materials['Clamp-Base']}
        />
        <mesh
          ref={fuelMeshRef}
          name="Fuel"
          castShadow
          receiveShadow
          geometry={nodes.Fuel.geometry}
          material={materials.Fuel}
          position={[-3, 1.055, -3]}>
          <group name="POI_LP_Fuel" position={[0.189, 6.969, -0.409]} />
          <group name="POS_LP_FuelCam" position={[7.793, 12.331, -7.84]} />
          <group name="TAR_LP_Fuel" position={[1.227, 8.173, 0.837]} />
        </mesh>
        <group name="POI_LP_Origin" position={[0, 3, 8]} />
        <group name="Shack" position={[-0.75, 0, 24.125]}>
          <group name="BalconyControl">
            <instances.Balcony name="Balcony01" scale={0.025} />
            <instances.Balcony name="Balcony01001" position={[0, 1.906, 0]} scale={0.025} />
            <instances.Balcony name="Balcony01002" position={[0, 3.857, 0]} scale={0.025} />
            <instances.Balcony name="Balcony01003" position={[0, 5.828, 0]} scale={0.025} />
            <instances.Balcony name="Balcony01004" position={[0, 7.696, 0]} scale={0.025} />
          </group>
          <group name="BeamControl">
            <instances.Beam name="Beam" scale={0.025} />
            <instances.BeamCross name="Beam-Cross01" scale={0.025} />
            <instances.BeamCross name="Beam-Cross02" position={[0, 3, 0]} scale={0.025} />
            <instances.BeamCross name="Beam-Cross03" position={[0, 7.014, 0]} scale={0.025} />
            <instances.BeamCross name="Beam-Cross04" position={[0, 11.024, 0]} scale={0.025} />
            <instances.Beam name="Beam001" position={[-3.334, 0, -0.002]} scale={0.025} />
            <instances.Beam name="Beam002" position={[-6.671, 0, 0.001]} scale={0.025} />
          </group>
          <group name="LampControl">
            <instances.Lamp name="Lamp01" scale={0.025} />
            <instances.Lamp name="Lamp02" position={[-6.69, 0, 0]} scale={0.025} />
          </group>
          <group name="POI_LP_Shack" position={[-1.359, 5.712, -1.625]} />
          <group name="POS_LP_ShackCam" position={[23.753, 19.023, -28.085]} />
          <group name="RackControl">
            <instances.Rack name="Rack01" scale={0.025} />
            <instances.Rack name="Rack02" position={[0, -1.929, 0]} scale={0.025} />
            <instances.Rack name="Rack03" position={[0, -3.858, 0]} scale={0.025} />
          </group>
          <mesh
            name="ShackMesh"
            castShadow
            receiveShadow
            geometry={nodes.ShackMesh.geometry}
            material={materials.Shack}
            scale={0.025}
          />
          <group name="TAR_LP_Shack" position={[-2.938, 9.016, -1.924]} />
        </group>
        <group name="Environment" position={[11.5, 0.068, 3.5]}>
          <mesh
            name="EnvironmentMesh"
            castShadow
            receiveShadow
            geometry={nodes.EnvironmentMesh.geometry}
            material={materials.Environment}
            position={[0, -0.01, 0]}
          />
          <group name="FenceControl" position={[0, -0.072, 0]}>
            <instances.Fence name="Fence01" position={[0, 0.063, 0]} />
            <instances.Fence name="Fence01001" position={[0, 0.063, -1]} />
            <instances.Fence name="Fence01002" position={[0, 0.063, -2]} />
            <instances.Fence name="Fence01003" position={[0, 0.063, -3]} />
            <instances.Fence name="Fence01004" position={[0, 0.063, -4]} />
            <instances.Fence name="Fence01005" position={[0, 0.063, -5]} />
            <instances.Fence
              name="Fence01006"
              position={[-1.75, 0.063, -1.55]}
              rotation={[0, 1.571, 0]}
            />
            <instances.Fence
              name="Fence01007"
              position={[-2.75, 0.063, -1.55]}
              rotation={[0, 1.571, 0]}
            />
            <instances.Fence
              name="Fence01008"
              position={[-3.75, 0.063, -1.55]}
              rotation={[0, 1.571, 0]}
            />
            <instances.Fence
              name="Fence01009"
              position={[-0.3, 0.063, 0.2]}
              rotation={[Math.PI, 0, Math.PI]}
            />
            <instances.Fence
              name="Fence01010"
              position={[-0.3, 0.063, 1.2]}
              rotation={[Math.PI, 0, Math.PI]}
            />
            <instances.Fence
              name="Fence01011"
              position={[-0.3, 0.063, 2.2]}
              rotation={[Math.PI, 0, Math.PI]}
            />
            <instances.Fence
              name="Fence01012"
              position={[-0.3, 0.063, 3.2]}
              rotation={[Math.PI, 0, Math.PI]}
            />
            <instances.Fence
              name="Fence01013"
              position={[-0.3, 0.063, 4.2]}
              rotation={[Math.PI, 0, Math.PI]}
            />
            <instances.Fence
              name="Fence01014"
              position={[-0.3, 0.063, 5.2]}
              rotation={[Math.PI, 0, Math.PI]}
            />
          </group>
          <instances.Lamppost name="Lamp_post01" position={[0, -0.01, 0]} />
          <instances.Lamppost
            name="Lamp_post02"
            position={[-34.977, -0.01, 11.543]}
            rotation={[Math.PI, 0, Math.PI]}
          />
          <group name="RailControl">
            <instances.Rail name="Rail01" position={[0, -0.02, 0]} />
            <instances.Rail name="Rail02" position={[0, -0.02, -3]} />
            <instances.Rail name="Rail03" position={[0, -0.02, -6]} />
            <instances.Rail name="Rail04" position={[0, -0.02, -9]} />
            <instances.Rail name="Rail05" position={[0, -0.02, -12]} />
            <instances.Rail name="Rail06" position={[0, -0.02, -15]} />
            <instances.Rail name="Rail07" position={[0, -0.02, -18]} />
          </group>
        </group>
      </group>
    </group>
  )
}

useGLTF.preload('/models/Gantry.glb')
